import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Box, Button, Checkbox, FormControl, FormControlLabel, Grid, IconButton, MenuItem, Paper, Radio, RadioGroup, Select, SelectChangeEvent, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { AuthUser } from '../../interfaces/Contexts';
import { getMentorsList, postadddocumentgoals, postaddgoalsmilestone, updateMilestoneGoal } from '../../Utils/apiService';
import routes from '../../Utils/routes';
import formData from './form.json';
import { useStyles } from './styles';
import KenLoader from '../../GlobalComponents/KenLoader';
import storageService from '../../Utils/storageService';

type FormValues = { [key: string]: string | boolean };

// Define the structure for Goal data
type Goal = {
  GoalName: string;
  Duration: string;
  ChooseMentor: string;
  ProgressMeasurement: string;
  ProgressPercentage: string;
  StartingValue?: string;
  CurrentValue?: string;
  Target?: string;
  GoalProgress: boolean;
  goalId?: string; // Optional for edit mode
};

// Define validation errors structure
type ValidationErrors = {
  [key: string]: string;
};

type GoalValidationErrors = {
  [index: number]: {
    [key: string]: string;
  };
};

const AddMilestones: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useHistory();
  const { state } = useLocation();
  console.log(state, 'state from location');

  const classes = useStyles();
  const [userData, setUserData] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>({});
  const [mentors, setMentors] = useState<any[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [selectedMentor, setSelectedMentor] = useState<{ id: string; name: string }>({ id: '', name: '' });
  const [selectedMentorId, setSelectedMentorId] = useState<string>('');
  const [formErrors, setFormErrors] = useState<ValidationErrors>({});
  const [goalErrors, setGoalErrors] = useState<GoalValidationErrors>({});
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [milestoneData, setMilestoneData] = useState<any>(null);
  const [goals, setGoals] = useState<Goal[]>([]);

  // Validation functions
  const validateMilestoneName = (name: string): string => {
    if (!name || name.trim() === '') {
      return 'Milestone name is required';
    }
    if (name.length < 3) {
      return 'Milestone name must be at least 3 characters long';
    }
    if (name.length > 150) {
      return 'Milestone name cannot exceed 150 characters';
    }
    if (!/^[a-zA-Z0-9\s\-_.,()]+$/.test(name)) {
      return 'Milestone name contains invalid characters';
    }
    return '';
  };

  const validateMentor = (mentorId: string): string => {
    if (!mentorId || mentorId.trim() === '') {
      return 'Please select a mentor';
    }
    return '';
  };

  const validateDuration = (duration: string): string => {
    if (!duration || duration.trim() === '') {
      return 'Please select a duration';
    }
    return '';
  };

  const validateProgressMethod = (method: string): string => {
    if (!method || method.trim() === '') {
      return 'Please select an update method';
    }
    return '';
  };

  const validateGoalName = (goalName: string): string => {
    if (!goalName || goalName.trim() === '') {
      return 'Goal name is required';
    }
    if (goalName.length < 3) {
      return 'Goal name must be at least 3 characters long';
    }
    if (goalName.length > 150) {
      return 'Goal name cannot exceed 150 characters';
    }
    return '';
  };

  const validateGoalDuration = (duration: string): string => {
    if (!duration || duration.trim() === '') {
      return 'Please select goal duration';
    }
    return '';
  };

  const validateGoalMentor = (mentorId: string): string => {
    if (!mentorId || mentorId.trim() === '') {
      return 'Please select a mentor for this goal';
    }
    return '';
  };

  const validateProgressMeasurement = (measurement: string): string => {
    if (!measurement || measurement.trim() === '') {
      return 'Please select how you want to measure progress';
    }
    return '';
  };

  const validateProgressPercentage = (percentage: string): string => {
    if (!percentage || percentage.trim() === '') {
      return 'Progress percentage is required';
    }
    const numValue = parseFloat(percentage.replace('%', ''));
    if (isNaN(numValue)) {
      return 'Please enter a valid percentage';
    }
    if (numValue < 0 || numValue > 100) {
      return 'Percentage must be between 0 and 100';
    }
    return '';
  };

  const validateStartingValue = (value: string): string => {
    if (!value || value.trim() === '') {
      return 'Starting value is required';
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return 'Please enter a valid number';
    }
    if (numValue < 0) {
      return 'Starting value cannot be negative';
    }
    return '';
  };

  const validateCurrentValue = (value: string): string => {
    if (!value || value.trim() === '') {
      return 'Current value is required';
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return 'Please enter a valid number';
    }
    if (numValue < 0) {
      return 'Current value cannot be negative';
    }
    return '';
  };

  const validateTarget = (value: string): string => {
    if (!value || value.trim() === '') {
      return 'Target value is required';
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return 'Please enter a valid number';
    }
    if (numValue <= 0) {
      return 'Target value must be greater than 0';
    }
    return '';
  };

  // Helper function to convert duration string to numeric value for comparison
  const getDurationValue = (duration: string): number => {
    switch (duration) {
      case '1 Month':
        return 1;
      case '3 Months':
        return 3;
      case '6 Months':
        return 6;
      case '1 Year':
        return 12;
      default:
        return 0;
    }
  };

  const validateGoalDurationAgainstMilestone = (goalDuration: string, milestoneDuration: string): string => {
    if (!goalDuration || !milestoneDuration) {
      return '';
    }
    
    const goalDurationValue = getDurationValue(goalDuration);
    const milestoneDurationValue = getDurationValue(milestoneDuration);
    
    if (goalDurationValue > milestoneDurationValue) {
      return `Goal duration (${goalDuration}) cannot be greater than milestone duration (${milestoneDuration})`;
    }
    
    return '';
  };

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    
    // Validate milestone fields
    const nameError = validateMilestoneName(formValues.Name as string || '');
    if (nameError) errors.Name = nameError;

    const mentorError = validateMentor(formValues.Category as string || '');
    if (mentorError) errors.Category = mentorError;

    const durationError = validateDuration(formValues.DocumentType as string || '');
    if (durationError) errors.DocumentType = durationError;

    const progressMethodError = validateProgressMethod(formValues['based on goals'] as string || '');
    if (progressMethodError) errors['based on goals'] = progressMethodError;

    setFormErrors(errors);

    // Validate goals
    const goalValidationErrors: GoalValidationErrors = {};
    let hasGoalErrors = false;

    // Check for duplicate goal names
    const goalNames = goals.map(goal => goal.GoalName.toLowerCase().trim()).filter(name => name !== '');
    const duplicateNames = goalNames.filter((name, index) => goalNames.indexOf(name) !== index);

    goals.forEach((goal, index) => {
      const goalErrors: ValidationErrors = {};
      
      const goalNameError = validateGoalName(goal.GoalName);
      if (goalNameError) goalErrors.GoalName = goalNameError;
      else if (duplicateNames.includes(goal.GoalName.toLowerCase().trim())) {
        goalErrors.GoalName = 'Goal name must be unique';
      }

      const goalDurationError = validateGoalDuration(goal.Duration);
      if (goalDurationError) goalErrors.Duration = goalDurationError;

      // Validate goal duration against milestone duration
      const milestoneDuration = formValues?.DocumentType as string || '';
      const durationComparisonError = validateGoalDurationAgainstMilestone(goal.Duration, milestoneDuration);
      if (durationComparisonError) {
        goalErrors.Duration = goalErrors.Duration || durationComparisonError;
      }

      const goalMentorError = validateGoalMentor(goal.ChooseMentor);
      if (goalMentorError) goalErrors.ChooseMentor = goalMentorError;

      const progressMeasurementError = validateProgressMeasurement(goal.ProgressMeasurement);
      if (progressMeasurementError) goalErrors.ProgressMeasurement = progressMeasurementError;

      // Conditional validation based on measurement type
      if (goal.ProgressMeasurement === 'Objective') {
        const startingValueError = validateStartingValue(goal.StartingValue || '');
        if (startingValueError) goalErrors.StartingValue = startingValueError;

        const currentValueError = validateCurrentValue(goal.CurrentValue || '');
        if (currentValueError) goalErrors.CurrentValue = currentValueError;

        const targetError = validateTarget(goal.Target || '');
        if (targetError) goalErrors.Target = targetError;
      } else if (goal.ProgressMeasurement === 'Subjective') {
        const progressPercentageError = validateProgressPercentage(goal.ProgressPercentage);
        if (progressPercentageError) goalErrors.ProgressPercentage = progressPercentageError;
      }

      if (Object.keys(goalErrors).length > 0) {
        goalValidationErrors[index] = goalErrors;
        hasGoalErrors = true;
      }
    });

    setGoalErrors(goalValidationErrors);

    // Check if we have at least one goal
    if (goals.length === 0) {
      enqueueSnackbar('Please add at least one goal', { variant: 'error' });
      return false;
    }

    return Object.keys(errors).length === 0 && !hasGoalErrors;
  };

  const clearFieldError = (fieldName: string) => {
    if (formErrors[fieldName]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const clearGoalError = (goalIndex: number, fieldName: string) => {
    if (goalErrors[goalIndex] && goalErrors[goalIndex][fieldName]) {
      setGoalErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors[goalIndex]) {
          delete newErrors[goalIndex][fieldName];
          if (Object.keys(newErrors[goalIndex]).length === 0) {
            delete newErrors[goalIndex];
          }
        }
        return newErrors;
      });
    }
  };

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const user = await storageService.getItem('user');
        setUserData(user ? JSON.parse(user) : null);
      } catch (error) {
        console.error('Error loading user data:', error);
        setUserData(null);
      }
    };
    loadUserData();
  }, []);

  // Handle edit mode initialization - FIRST PRIORITY
  useEffect(() => {
    console.log('=== CHECKING FOR EDIT MODE ===');
    console.log('State:', state);
    
    if (state && (state as any).dataFor === 'editMilestone' && (state as any).data) {
      console.log('=== EDIT MODE DETECTED ===');
      console.log('Edit data:', (state as any).data);
      
      setIsEditMode(true);
      setMilestoneData((state as any).data);
      
      // Populate form values for milestone
      const milestone = (state as any).data;
      console.log('Processing milestone for edit:', milestone);
      console.log('Goals in milestone:', milestone?.mileStoneGoals);
      
      // Handle duration mapping - API returns inconsistent formats
      // Check if this is a goal or milestone data
      let mappedDuration = '';
      if (milestone?.mileStoneDuration) {
        // This is milestone data
        mappedDuration = milestone?.mileStoneDuration || '';
      } else if (milestone?.goalDuration) {
        // This is goal data
        mappedDuration = milestone?.goalDuration || '';
      }
      
      if (mappedDuration === '1 month') {
        mappedDuration = '1 Month';
      }
      
      const formData = {
        Name: milestone?.mileStoneName || milestone?.goalName || '',
        Category: milestone?.mentorshipId || '', 
        DocumentType: mappedDuration,
        'based on goals': milestone?.mileStoneProgressUpdateMethod || milestone?.goalProgressUpdateMethod || '',
        'starting value': milestone?.mileStoneProgressStartingValue?.toString() || milestone?.goalProgressStartingValue?.toString() || '0',
        'current value': milestone?.mileStoneProgressCurrentValue?.toString() || milestone?.goalProgressCurrentValue?.toString() || '0',
        target: milestone?.mileStoneProgressTarget?.toString() || milestone?.goalProgressTarget?.toString() || '100',
        Description: milestone?.mentorship?.description || '',
        SupportingDocument: milestone?.supportingDocumentUrl || '', // Now populated from API
        SelectedMentorName: milestone?.mentorName || ''
      };
      
      console.log('Setting form values:', formData);
      setFormValues(formData);

      // Set fileName if supporting document exists
      if (milestone?.supportingDocumentUrl) {
        // Extract filename from URL (or use a descriptive name)
        const urlParts = milestone?.supportingDocumentUrl.split('/');
        const extractedFileName = urlParts[urlParts.length - 1] || 'Existing Document';
        console.log('Setting existing file name:', extractedFileName);
        console.log('Supporting document URL:', milestone?.supportingDocumentUrl);
        setFileName(extractedFileName);
      } else {
        console.log('No supporting document URL found in milestone data');
      }

        // Populate goals with a slight delay to ensure state is stable
        setTimeout(() => {
          if (milestone?.mileStoneGoals && Array.isArray(milestone?.mileStoneGoals) && milestone?.mileStoneGoals.length > 0) {
            console.log('=== POPULATING GOALS ===');
            console.log('Number of goals to populate:', milestone?.mileStoneGoals.length);
            
            const populatedGoals = milestone?.mileStoneGoals.map((goal: any, index: number) => {
              console.log(`Processing goal ${index + 1}:`, goal);
              console.log(`Goal progress values - Current: ${goal.goalProgressCurrentValue}, Target: ${goal.goalProgressTarget}`);
              
              // Handle goal duration mapping
              let mappedGoalDuration = goal?.goalDuration as string || '';
              if (mappedGoalDuration === '1 month') {
                mappedGoalDuration = '1 Month';
              }
              
              // Use goalProgressPercentage if available, otherwise calculate from current/target values
              let progressPercentage = '';
              if (goal.goalProgressPercentage !== null && goal.goalProgressPercentage !== undefined) {
                // Use the directly provided percentage from API
                progressPercentage = goal.goalProgressPercentage.toString();
                console.log(`✅ Using direct progress percentage: ${progressPercentage}%`);
              } else if (goal.goalProgressCurrentValue !== undefined && goal.goalProgressTarget !== undefined) {
                // Fallback to calculation if direct percentage not available
                const current = parseFloat(goal.goalProgressCurrentValue);
                const target = parseFloat(goal.goalProgressTarget);
                console.log(`Goal ${index + 1} progress calculation fallback:`, { 
                  currentValue: goal.goalProgressCurrentValue, 
                  targetValue: goal.goalProgressTarget,
                  currentParsed: current, 
                  targetParsed: target 
                });
                if (!isNaN(current) && !isNaN(target) && target > 0) {
                  const percentage = Math.round((current / target) * 100);
                  progressPercentage = percentage.toString();
                  console.log(`✅ Calculated progress percentage: ${progressPercentage}%`);
                } else {
                  console.log(`❌ Invalid progress values: current=${current}, target=${target}`);
                  progressPercentage = '0';
                }
              } else {
                console.log(`❌ Missing both direct percentage and calculation values`);
                progressPercentage = '0';
              }
              
              const populatedGoal = {
                GoalName: goal.goalName || '',
                Duration: mappedGoalDuration,
                ChooseMentor: milestone?.mentorshipId || '', 
                ProgressMeasurement: goal.goalProgressUpdateMethod || 'Subjective',
                ProgressPercentage: progressPercentage,
                StartingValue: goal.goalProgressStartingValue?.toString() || '0',
                CurrentValue: goal.goalProgressCurrentValue?.toString() || '0',
                Target: goal.goalProgressTarget?.toString() || '100',
                GoalProgress: true, // Default to true since API doesn't provide this field
                goalId: goal.id // Store the goal ID for updates
              };
              
              console.log(`Populated goal ${index + 1}:`, populatedGoal);
              return populatedGoal;
            });
            
            console.log('Setting goals to:', populatedGoals);
            setGoals(populatedGoals);
          } else {
            console.log('No goals found in milestone data or goals is not an array');
            console.log('Goals data type:', typeof milestone?.mileStoneGoals);
            console.log('Goals is array:', Array.isArray(milestone?.mileStoneGoals));
            console.log('Goals length:', milestone?.mileStoneGoals?.length);
            
            // Set at least one empty goal for edit mode
            setGoals([{
              GoalName: '',
              Duration: '',
              ChooseMentor: '',
              ProgressMeasurement: '',
              ProgressPercentage: '',
              StartingValue: '',
              CurrentValue: '',
              Target: '',
              GoalProgress: false,
            }]);
          }
        }, 100); // 100ms delay to ensure other useEffects don't interfere
    } else if (state && (state as any).dataFor === 'editGoal' && (state as any).data) {
      console.log('=== EDIT GOAL MODE DETECTED ===');
      console.log('Edit goal data:', (state as any).data);
      console.log('Milestone data:', (state as any).milestoneData);
      console.log('Full state:', state);
      console.log('Goal data keys:', Object.keys((state as any).data || {}));
      console.log('Milestone data keys:', Object.keys((state as any).milestoneData || {}));
      console.log('Goal name:', (state as any).data?.goalName);
      console.log('Goal duration:', (state as any).data?.goalDuration);
      console.log('Goal progress:', (state as any).data?.goalProgressPercentage);
      
      setIsEditMode(true);
      const goalData = (state as any).data;
      const milestoneData = (state as any).milestoneData;
      
      // Check if goal data is valid
      if (!goalData || Object.keys(goalData).length === 0) {
        console.error('❌ Goal data is empty or invalid:', goalData);
        return;
      }
      
      console.log('✅ Goal data is valid:', goalData);
      
      // Set milestone data for context
      setMilestoneData(milestoneData);
      
      // Populate form values for milestone (from milestoneData)
      const milestone = milestoneData;
      console.log('Processing milestone for goal edit:', milestone);
      
      // Handle duration mapping - API returns inconsistent formats
      // Check if this is a goal or milestone data
      let mappedDuration = '';
      if (milestone?.mileStoneDuration) {
        // This is milestone data
        mappedDuration = milestone?.mileStoneDuration || '';
      } else if (milestone?.goalDuration) {
        // This is goal data
        mappedDuration = milestone?.goalDuration || '';
      }
      
      if (mappedDuration === '1 month') {
        mappedDuration = '1 Month';
      }
      
      const formData = {
        Name: milestone?.mileStoneName || '',
        Category: milestone?.mentorshipId || '', 
        DocumentType: mappedDuration,
        'based on goals': milestone?.mileStoneProgressUpdateMethod || '',
        'starting value': milestone?.mileStoneProgressStartingValue?.toString() || '0',
        'current value': milestone?.mileStoneProgressCurrentValue?.toString() || '0',
        target: milestone?.mileStoneProgressTarget?.toString() || '100',
        Description: milestone?.mentorship?.description || '',
        SupportingDocument: milestone?.supportingDocumentUrl || '',
        SelectedMentorName: milestone?.mentorName || ''
      };
      
      console.log('Setting form values for goal edit:', formData);
      setFormValues(formData);

      // Set fileName if supporting document exists
      if (milestone?.supportingDocumentUrl) {
        const urlParts = milestone?.supportingDocumentUrl?.split('/');
        const extractedFileName = urlParts[urlParts.length - 1] || 'Existing Document';
        console.log('Setting existing file name for goal edit:', extractedFileName);
        setFileName(extractedFileName);
      }

      // Populate goals with the specific goal to edit immediately
      console.log('=== POPULATING GOAL FOR EDIT ===');
      console.log('Goal to edit:', goalData);
      
      // Handle goal duration mapping
      let mappedGoalDuration = goalData?.goalDuration || '';
      if (mappedGoalDuration === '1 month') {
        mappedGoalDuration = '1 Month';
      }
      
      // Use goalProgressPercentage if available, otherwise calculate from current/target values
      let progressPercentage = '';
      if (goalData?.goalProgressPercentage !== null && goalData?.goalProgressPercentage !== undefined) {
        progressPercentage = goalData.goalProgressPercentage.toString();
        console.log(`✅ Using direct progress percentage: ${progressPercentage}%`);
      } else if (goalData?.goalProgressCurrentValue !== undefined && goalData?.goalProgressTarget !== undefined) {
        const current = parseFloat(goalData.goalProgressCurrentValue);
        const target = parseFloat(goalData.goalProgressTarget);
        if (!isNaN(current) && !isNaN(target) && target > 0) {
          const percentage = Math.round((current / target) * 100);
          progressPercentage = percentage.toString();
          console.log(`✅ Calculated progress percentage: ${progressPercentage}%`);
        } else {
          progressPercentage = '0';
        }
      } else {
        progressPercentage = '0';
      }
      
      const populatedGoal = {
        GoalName: goalData?.goalName || '',
        Duration: mappedGoalDuration,
        ChooseMentor: milestone?.mentorshipId || '', 
        ProgressMeasurement: goalData?.goalProgressUpdateMethod || 'Subjective',
        ProgressPercentage: progressPercentage,
        StartingValue: goalData?.goalProgressStartingValue?.toString() || '0',
        CurrentValue: goalData?.goalProgressCurrentValue?.toString() || '0',
        Target: goalData?.goalProgressTarget?.toString() || '100',
        GoalProgress: true,
        goalId: goalData?.id // Store the goal ID for updates
      };
      
      console.log('Setting single goal for edit:', populatedGoal);
      setGoals([populatedGoal]);
    } else {
      console.log('Not in edit mode or no edit data found');
      setIsEditMode(false);
    }
  }, [state]);

  // Verify mentor exists in dropdown when mentors are loaded in edit mode
  useEffect(() => {
    if (isEditMode && milestoneData && mentors.length > 0 && milestoneData.mentorshipId) {
      // Verify the mentor exists in the dropdown
      const mentor = mentors.find(m => m.mentorshipId === milestoneData.mentorshipId);
      if (mentor) {
        console.log('Found mentor for edit mode:', mentor);
        // The form values should already be set, this is just verification
        if (!formValues.Category) {
          setFormValues(prev => ({
            ...prev,
            Category: milestoneData.mentorshipId,
            SelectedMentorName: mentor.name
          }));
        }
      } else {
        console.warn('Mentor not found in dropdown:', milestoneData.mentorshipId);
      }
    }
  }, [mentors, isEditMode, milestoneData, formValues.Category]);

  // Initialize goals array when not in edit mode - SECOND PRIORITY
  useEffect(() => {
    console.log('=== GOALS INITIALIZATION CHECK ===');
    console.log('Is edit mode:', isEditMode);
    console.log('Goals length:', goals.length);
    console.log('State:', state);
    
    // Only initialize empty goals if we're not in edit mode AND we don't have any goals yet
    // Don't include goals.length as dependency to avoid conflicts with edit mode
    if (!isEditMode && goals.length === 0 && (!state || (state as any).dataFor !== 'editMilestone')) {
      console.log('Initializing empty goals for create mode');
      setGoals([
        {
          GoalName: '',
          Duration: '',
          ChooseMentor: '',
          ProgressMeasurement: '',
          ProgressPercentage: '',
          StartingValue: '',
          CurrentValue: '',
          Target: '',
          GoalProgress: false,
        }
      ]);
    }
  }, [isEditMode, state]); // Removed goals.length dependency

  // Debug goals state changes
  useEffect(() => {
    console.log('=== GOALS STATE CHANGED ===');
    console.log('Goals count:', goals.length);
    console.log('Goals data:', goals);
  }, [goals]);

  // Debug logging for goals state changes
  useEffect(() => {
    console.log('=== GOALS STATE CHANGED ===');
    console.log('Current goals:', goals);
    console.log('Goals length:', goals.length);
    console.log('Is edit mode:', isEditMode);
  }, [goals, isEditMode]);

  // Debug SupportingDocument form value changes
  useEffect(() => {
    console.log('=== FORM VALUES CHANGED ===');
    console.log('SupportingDocument value:', formValues.SupportingDocument);
    console.log('fileName state:', fileName);
  }, [formValues.SupportingDocument, fileName]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    // Trigger the file input click programmatically
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Fetch mentors list when the component mounts
  useEffect(() => {
    const fetchMentors = async () => {
      try {
        const response = await getMentorsList();
        if (response.status === 'true') {
          setMentors(response.result); // Store the fetched mentors in state
        }
      } catch (error) {
        console.error('Error fetching mentors:', error);
      }
    };

    fetchMentors();
  }, []);

  const handleMentorChange = (event: SelectChangeEvent<string | true>) => {
    const mentorId = event.target.value as string;

    // Find the selected mentor
    const mentor = mentors.find((mentor) => mentor.mentorshipId === mentorId);

    if (mentor) {
      setFormValues((prevValues) => ({
        ...prevValues,
        Category: mentorId, // Store the mentor ID
        SelectedMentorName: mentor.name, // Optionally store the mentor name
      }));
    }
  };

  const handleChange = (name: string, value: string | boolean) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
    // Clear error when user starts typing/selecting
    clearFieldError(name);

    // If milestone duration changes, revalidate all goal durations
    if (name === 'DocumentType') {
      const milestoneDuration = value as string;
      const updatedGoalErrors = { ...goalErrors };
      let hasChanges = false;

      goals.forEach((goal, index) => {
        if (goal.Duration) {
          const durationError = validateGoalDurationAgainstMilestone(goal.Duration, milestoneDuration);
          if (durationError) {
            if (!updatedGoalErrors[index]) {
              updatedGoalErrors[index] = {};
            }
            updatedGoalErrors[index].Duration = durationError;
            hasChanges = true;
          } else {
            // Clear duration error if validation passes
            if (updatedGoalErrors[index] && updatedGoalErrors[index].Duration) {
              delete updatedGoalErrors[index].Duration;
              if (Object.keys(updatedGoalErrors[index]).length === 0) {
                delete updatedGoalErrors[index];
              }
              hasChanges = true;
            }
          }
        }
      });

      if (hasChanges) {
        setGoalErrors(updatedGoalErrors);
      }
    }
  };

  const handleGoalChange = (index: number, name: string, value: string | boolean) => {
    const updatedGoals = [...goals];
    updatedGoals[index] = {
      ...updatedGoals[index],
      [name]: value,
    };
    setGoals(updatedGoals);
    // Clear error when user starts typing/selecting
    clearGoalError(index, name);

    // If goal duration changes, validate against milestone duration
    if (name === 'Duration') {
      const milestoneDuration = formValues?.DocumentType as string || '';
      if (milestoneDuration) {
        const durationError = validateGoalDurationAgainstMilestone(value as string, milestoneDuration);
        if (durationError) {
          setGoalErrors(prev => ({
            ...prev,
            [index]: {
              ...prev[index],
              Duration: durationError
            }
          }));
        }
      }
    }
  };
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file: any = event.target.files?.[0];

    if (!file) return;

    // Check if the file is a PDF
    if (file.type !== "application/pdf") {
      enqueueSnackbar("Only PDF files are allowed!", { variant: "error" });
      return;
    }

    // Check if the file size exceeds 5MB
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      enqueueSnackbar("File size should not exceed 5MB!", { variant: "error" });
      return;
    }

    setFileName(file.name);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await postadddocumentgoals(formData);

      if (response.status === "true" && response.result.success) {
        const documentUrl = response.result.message;
        setFormValues((prevValues) => ({
          ...prevValues,
          SupportingDocument: documentUrl,
        }));
      } else {
        console.error("Error uploading document:", response.result.message);
      }
    } catch (error) {
      console.error("Error uploading document:", error);
    }
  };


  // Handle adding a new goal
  const handleAddGoal = () => {
    if (goals.length >= 10) {
      enqueueSnackbar('Maximum 10 goals allowed per milestone', { variant: 'warning' });
      return;
    }
    const newGoal: Goal = {
      GoalName: '',
      Duration: '',
      ChooseMentor: '',
      ProgressMeasurement: '',
      ProgressPercentage: '',
      StartingValue: '',
      CurrentValue: '',
      Target: '',
      GoalProgress: false,
    };
    setGoals((prevGoals) => [...prevGoals, newGoal]);
  };

  // Handle removing a goal
  const handleRemoveGoal = (index: number) => {
    if (goals.length <= 1) {
      enqueueSnackbar('At least one goal is required', { variant: 'warning' });
      return;
    }
    const updatedGoals = goals.filter((_, goalIndex) => goalIndex !== index);
    setGoals(updatedGoals);
    
    // Clear any errors for removed goal
    if (goalErrors[index]) {
      setGoalErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        // Reindex the remaining errors
        const reindexedErrors: GoalValidationErrors = {};
        Object.keys(newErrors).forEach(key => {
          const oldIndex = parseInt(key);
          if (oldIndex > index) {
            reindexedErrors[oldIndex - 1] = newErrors[oldIndex];
          } else {
            reindexedErrors[oldIndex] = newErrors[oldIndex];
          }
        });
        return reindexedErrors;
      });
    }
  };

  const handleSubmit = async () => {
    // Validate form before submission
    if (!validateForm()) {
      enqueueSnackbar('Please fix the validation errors before submitting', { variant: 'error' });
      return;
    }

    console.log('Form submission started');
    console.log('Form values:', formValues);
    console.log('SupportingDocument value:', formValues.SupportingDocument);
    console.log('All form value keys:', Object.keys(formValues));
    console.log('Form values type:', typeof formValues.SupportingDocument);

    const baseData = {
      mentorId: formValues['Category'],
      mentorName: formValues['SelectedMentorName'],
      menteeId: userData?.accountId,
      milestoneName: formValues.Name,
      description: formValues.Description || "",
      milestoneDuration: formValues?.DocumentType,
      progressUpdateMethod: formValues['based on goals'],
      startValue: formValues['starting value'] || "0",
      currentValue: formValues['current value'] || "0",
      progressTarget: formValues['target'] || "100",
      supportingDocumentUrl: formValues.SupportingDocument || "",
      goals: goals.map((goal) => ({
        goalName: goal.GoalName,
        goalDuration: goal.Duration,
        progressUpdateMethod: goal.ProgressMeasurement || "Subjective",
        progressPercentage: goal.ProgressMeasurement === 'Subjective' ? parseFloat(goal.ProgressPercentage?.replace('%', '') || '0') : 0,
        addProgressToMilestone: goal.GoalProgress !== undefined ? goal.GoalProgress : true,
        progressStartingValue: goal.ProgressMeasurement === 'Objective' ? parseFloat(goal.StartingValue || '0') : 0,
        progressCurrentValue: goal.ProgressMeasurement === 'Objective' ? parseFloat(goal.CurrentValue || '0') : 0,
        progressTarget: goal.ProgressMeasurement === 'Objective' ? parseFloat(goal.Target || '100') : 100,
        ...(goal.goalId && { goalId: goal.goalId }) // Include goalId for existing goals
      })),
    };

    console.log('Base data supportingDocumentUrl:', baseData.supportingDocumentUrl);

    // Add milestone ID for edit mode
    const dataToSubmit = isEditMode && milestoneData 
      ? { ...baseData, milestoneId: milestoneData.id }
      : baseData;

    console.log('Data to Submit:', dataToSubmit);
    console.log('Is Edit Mode:', isEditMode);
    console.log('Milestone Data:', milestoneData);
    console.log('Final supportingDocumentUrl being sent:', dataToSubmit.supportingDocumentUrl);
    setLoading(true);

    try {
      // Use different API endpoints for create vs update
      const response = isEditMode 
        ? await updateMilestoneGoal(dataToSubmit)
        : await postaddgoalsmilestone(dataToSubmit);
      
      console.log('API Response:', response);
      
      // Check if the API call was successful
      if (response && response.status === 'true' && response.result && response.result.success) {
        // Reset form on successful submission
        setFormValues({
          Name: '',
          DocumentType: '',
          'based on goals': '',
          'starting value': '',
          'current value': '',
          target: '',
          SupportingDocument: '',
          Description: '',
        });
        
        console.log('Resetting fileName after successful submission');
        setFileName('');

        setGoals([
          {
            GoalName: '',
            Duration: '',
            ChooseMentor: '',
            ProgressMeasurement: '',
            ProgressPercentage: '',
            StartingValue: '',
            CurrentValue: '',
            Target: '',
            GoalProgress: false,
          },
        ]);

        // Clear validation errors
        setFormErrors({});
        setGoalErrors({});

        setLoading(false);
        navigate.push(routes.goalsAndMilestones);
        enqueueSnackbar(
          isEditMode 
            ? 'Your Milestone and Goals have been updated successfully.' 
            : 'Your Milestone and Goals are added successfully.', 
          { variant: 'success' }
        );
      } else {
        // Handle API failure
        setLoading(false);
        const errorMessage = response?.result?.message || response?.message || 
          `Failed to ${isEditMode ? 'update' : 'create'} milestone and goals`;
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    } catch (error) {
      console.error('Error submitting data:', error);
      setLoading(false);
      enqueueSnackbar('Error submitting the form. Please try again.', { variant: 'error' });
    }
  };
  return (
    <>
      {loading ? (
        <KenLoader />
      ) : (
        <Box sx={{ m: { xs: 0, sm: 'auto' } }}>
          <Paper className={classes.card} sx={{ p: { xs: 0, sm: 3 }, pb: { xs: 10, sm: 3 }, borderRadius: { xs: 0, sm: '8px' } }}>
            <Grid container spacing={{ xs: 1, sm: 3 }} sx={{ p: { xs: 0, sm: 0 } }}>
              <Grid item xs={12} sm={5} md={5} xl={6}>
                <Box>
                  <Typography className={classes.milestonetitle}>{isEditMode ? 'Edit Milestone details' : 'Milestone details'}</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '16px', sm: '24px', ultraD: '18px' }, mt: { xs: '16px', sm: '24px', ultraD: '18px' } }}>
                    {formData.fields.map((field) => {
                      if (field.type === 'text' && field.name === 'Name') {
                        return (
                          <Box key={field.name}>
                            <Typography className={classes.label} sx={{ mb: { xs: '9px', ultraHD: '12px' } }}>
                              {field.label}<span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <TextField
                              fullWidth
                              type={field.type}
                              placeholder={field.placeholder}
                              value={formValues[field.name] || ''}
                              onChange={(e) => {
                                if (e.target.value.length <= 150) {
                                  handleChange(field.name, e.target.value);
                                }
                              }}
                              error={!!formErrors[field.name] || (typeof formValues[field.name] === 'string' && (formValues[field.name] as string).length > 150)}
                              helperText={formErrors[field.name] || `${(formValues[field.name] as string || '').length}/150 characters`}
                              sx={{
                                fontSize: { xs: '12px', ultraHD: '16px' },
                                borderRadius: { xs: '6px', ultraHD: '8px' }
                              }}
                              InputProps={{
                                sx: {
                                  height: { xs: '45px', ultraHD: '60px' },
                                  borderRadius: { xs: '6px', ultraHD: '8px' }
                                }
                              }}
                            />
                          </Box>
                        );
                      }
                    })}


                    <Grid container spacing={2}>
                      {formData.fields.map((field) => {
                        if (field.type === 'select' && (field.name === 'Category' || field.name === 'Priority')) {
                          return (
                            <Grid item xs={12} sm={6} key={field.name}>
                              <FormControl fullWidth error={!!formErrors[field.name]}>
                                <Typography className={classes.label} sx={{ mb: { xs: '9px', ultraHD: '12px' } }}>
                                  {field.label}{field.name === 'Category' ? <span style={{ color: 'red' }}>*</span> : ''}
                                </Typography>
                                <Select
                                  fullWidth
                                  id={field.name}
                                  value={formValues[field.name] || ''}
                                  labelId={`${field.name}-label`}
                                  onChange={(e) => (field.name === 'Category' ? handleMentorChange(e) : handleChange(field.name, e.target.value))}
                                  placeholder={field.placeholder}
                                  sx={{
                                    height: { xs: '45px', ultraHD: '60px' },
                                    fontSize: { xs: '12px', ultraHD: '16px' },
                                    borderRadius: { xs: '6px', ultraHD: '8px' },
                                  }}
                                >
                                  {field.name === 'Category' && mentors
                                    ? mentors.map((mentor) => (
                                      <MenuItem key={mentor.id} value={mentor.mentorshipId} sx={{ fontSize: { xs: '12px', ultraHD: '16px' } }}>
                                        {mentor.name}
                                      </MenuItem>
                                    ))
                                    : Array.isArray(field.options) &&
                                    field.options.map((option, index) => (
                                      <MenuItem key={index} value={option} sx={{ fontSize: { xs: '12px', ultraHD: '16px' } }}>
                                        {option}
                                      </MenuItem>
                                    ))}
                                </Select>
                                {formErrors[field.name] && (
                                  <Typography variant="caption" color="error" sx={{ mt: 0.5, fontSize: '12px' }}>
                                    {formErrors[field.name]}
                                  </Typography>
                                )}
                              </FormControl>
                            </Grid>
                          );
                        }
                        return null;
                      })}
                    </Grid>
                    <Grid container spacing={2}>
                      {formData.fields.map((field) => {
                        if (field.type === 'select' && field.name === 'DocumentType') {
                          return (
                            <Grid item xs={12} sm={6} key={field.name}>
                              <FormControl fullWidth error={!!formErrors[field.name]}>
                                <Typography className={classes.label} sx={{ mb: { xs: '9px', ultraHD: '12px' } }}>
                                  {field.label}<span style={{ color: 'red' }}>*</span>
                                </Typography>
                                <Select
                                  id={field.name}
                                  labelId={`${field.name}-label`}
                                  value={formValues[field.name] || ''}
                                  onChange={(e) => handleChange(field.name, e.target.value)}
                                  className={classes.textfield}
                                  placeholder={field.label}
                                  sx={{
                                    height: { xs: '45px', ultraHD: '60px' },
                                    fontSize: { xs: '12px', ultraHD: '16px' },
                                    borderRadius: { xs: '6px', ultraHD: '8px' },
                                  }}
                                >
                                  {Array.isArray(field.options) &&
                                    field.options.map((option, index) => (
                                      <MenuItem key={index} value={option} sx={{ fontSize: { xs: '12px', ultraHD: '16px' } }}>
                                        {option}
                                      </MenuItem>
                                    ))}
                                </Select>
                                {formErrors[field.name] && (
                                  <Typography variant="caption" color="error" sx={{ mt: 0.5, fontSize: '12px' }}>
                                    {formErrors[field.name]}
                                  </Typography>
                                )}
                              </FormControl>
                            </Grid>
                          );
                        }

                        if (field.type === 'file' && field.name === 'SupportingDocument') {
                          return (
                            <Grid item xs={12} sm={6} key={field.name}>
                              <Typography className={classes.label} sx={{ mb: { xs: '9px', ultraHD: '12px' } }}>
                                Upload supporting document
                              </Typography>
                              <Button
                                aria-label="Upload File"
                                title={fileName ? fileName : "Upload File"}
                                onClick={handleButtonClick}
                                sx={{
                                  height: { xs: '45px', ultraHD: '60px' },
                                  color: fileName ? '#4c155f' : '#B0B0B0',
                                  border: '1px solid #B0B0B0',
                                  fontWeight: fileName ? '500' : '400',
                                  textDecoration: fileName ? 'underline' : 'none',
                                  fontSize: { xs: '12px', ultraHD: '16px' },
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                }}
                                variant="outlined"
                                fullWidth
                                endIcon={<FileUploadOutlinedIcon />}
                              >
                                {fileName ? (fileName.length > 20 ? `${fileName.substring(0, 17)}...` : fileName) : field.label}
                                <input type="file" ref={fileInputRef} style={{ display: 'none', width: '100%' }} onChange={(e) => handleFileChange(e)} />
                              </Button>
                            </Grid>
                          );
                        }

                        return null;
                      })}
                    </Grid>

                    {/* How will you measure progress */}
                    <Box sx={{ mt: { xs: '24px', sm: '32px' } }}>
                      <Typography className={classes.milestonetitle}>How will you measure progress</Typography>
                    </Box>
                    <Grid container spacing={2}>
                      {formData.fields.map((field) => {
                        if (field.type === 'select' && (field.name === 'based on goals')) {
                          return (
                            <Grid item xs={12} sm={6} key={field.name}>
                              <FormControl fullWidth error={!!formErrors[field.name]}>
                                <Typography className={classes.label} sx={{ mb: { xs: '9px', ultraHD: '12px' } }}>
                                  {field.label}{field.name === 'based on goals' ? <span style={{ color: 'red' }}>*</span> : ''}
                                </Typography>
                                <Select
                                  id={field.name}
                                  labelId={`${field.name}-label`}
                                  value={formValues[field.name] || ''}
                                  className={classes.textfield}
                                  onChange={(e) => handleChange(field.name, e.target.value)}
                                  placeholder={field.label}
                                  sx={{
                                    height: { xs: '45px', ultraHD: '60px' },
                                    fontSize: { xs: '12px', ultraHD: '16px' },
                                    borderRadius: { xs: '6px', ultraHD: '8px' },
                                  }}
                                >
                                  {Array.isArray(field.options) &&
                                    field.options.map((option, index) => (
                                      <MenuItem key={index} value={option} sx={{ fontSize: { xs: '12px', ultraHD: '16px' } }}>
                                        {option}
                                      </MenuItem>
                                    ))}
                                </Select>
                                {formErrors[field.name] && (
                                  <Typography variant="caption" color="error" sx={{ mt: 0.5, fontSize: '12px' }}>
                                    {formErrors[field.name]}
                                  </Typography>
                                )}
                              </FormControl>
                            </Grid>
                          );
                        }
                        if (field.type === 'text' && field.name === 'starting value') {
                          return (
                            <Grid item xs={12} sm={6} key={field.name}>
                              <Typography className={classes.label} sx={{ mb: { xs: '9px', ultraHD: '12px' } }}>
                                {field.label}
                              </Typography>
                              <TextField
                                fullWidth
                                type="number"
                                placeholder={field.placeholder}
                                value={formValues[field.name] || ''}
                                onChange={(e) => handleChange(field.name, e.target.value)}
                                error={!!formErrors[field.name]}
                                helperText={formErrors[field.name] || ''}
                                sx={{
                                  fontSize: { xs: '12px', ultraHD: '16px' },
                                  borderRadius: { xs: '6px', ultraHD: '8px' }
                                }}
                                InputProps={{
                                  sx: {
                                    height: { xs: '45px', ultraHD: '60px' },
                                    borderRadius: { xs: '6px', ultraHD: '8px' }
                                  }
                                }}
                              />
                            </Grid>
                          );
                        }
                        return null;
                      })}
                    </Grid>
                    <Grid container spacing={2}>
                      {formData.fields.map((field) => {
                        if (field.type === 'text' && (field.name === 'current value' || field.name === 'target')) {
                          return (
                            <Grid item xs={12} sm={6} key={field.name}>
                              <Typography className={classes.label} sx={{ mb: { xs: '9px', ultraHD: '12px' } }}>
                                {field.label}
                              </Typography>
                              <TextField
                                fullWidth
                                type="number"
                                placeholder={field.placeholder}
                                value={formValues[field.name] || ''}
                                onChange={(e) => handleChange(field.name, e.target.value)}
                                error={!!formErrors[field.name]}
                                helperText={formErrors[field.name] || ''}
                                sx={{
                                  fontSize: { xs: '12px', ultraHD: '16px' },
                                  borderRadius: { xs: '6px', ultraHD: '8px' }
                                }}
                                InputProps={{
                                  sx: {
                                    height: { xs: '45px', ultraHD: '60px' },
                                    borderRadius: { xs: '6px', ultraHD: '8px' }
                                  }
                                }}
                              />
                            </Grid>
                          );
                        }
                        return null;
                      })}
                    </Grid>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={0.5} sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Box className={classes.verticalDivider} />
              </Grid>

              {/* Goal Section */}
              <Grid item xs={12} sm={5.5} sx={{ display: 'flex', flexDirection: 'column', height: { xs: 'auto', sm: '100%' } }}>
                <Box sx={{ 
                  height: { xs: 'auto', sm: '100%' }, 
                  overflow: { xs: 'visible', sm: 'auto' }, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: { xs: 'flex-start', sm: 'space-between' },
                  pb: { xs: '180px', sm: 0 } // Add bottom padding on mobile to prevent overlap
                }}>
                  <Box sx={{ flex: { xs: 'none', sm: 1 } }}>
                    {goals.map((goal, index) => (
                      <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: index < goals.length - 1 ? '24px' : '0' }}>
                        {/* Goal Title */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography className={classes.milestonetitle}>Goal #{index + 1}</Typography>
                          {goals.length > 1 && (
                            <IconButton
                              onClick={() => handleRemoveGoal(index)}
                              color="error"
                              size="small"
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'rgba(244, 67, 54, 0.04)',
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>

                        {/* Goal Name TextField */}
                        {formData.fields.map((field) => {
                          if (field.type === 'text' && field.name === 'GoalName') {
                            return (
                              <>
                                <Typography className={classes.label} sx={{ mb: { xs: '9px', ultraHD: '12px' } }}>
                                  {field.label}<span style={{ color: 'red' }}>*</span>
                                </Typography>
                                <TextField
                                  className={classes.textfield}
                                  key={field.name}
                                  fullWidth
                                  value={goal.GoalName || ''}
                                  onChange={(e) => {
                                    if (e.target.value.length <= 150) {
                                      handleGoalChange(index, 'GoalName', e.target.value);
                                    }
                                  }}
                                  placeholder={field.placeholder}
                                  error={!!(goalErrors[index] && goalErrors[index]['GoalName']) || !!(goal.GoalName && goal.GoalName.length > 150)}
                                  helperText={goalErrors[index] && goalErrors[index]['GoalName'] ? goalErrors[index]['GoalName'] : `${(goal.GoalName || '').length}/150 characters`}
                                  InputProps={{
                                    sx: {
                                      height: { xs: '45px', ultraHD: '60px' },
                                      borderRadius: { xs: '6px', ultraHD: '8px' },
                                    },
                                  }}
                                />
                              </>
                            );
                          }
                          return null;
                        })}
                        <Grid container spacing={2}>
                          {formData.fields.map((field) => {
                            if (field.type === 'select' && (field.name === 'Duration' || field.name === 'ChooseMentor')) {
                              return (
                                <Grid item xs={12} sm={6} key={field.name}>
                                  <FormControl fullWidth error={!!(goalErrors[index] && goalErrors[index][field.name])}>
                                    <Typography className={classes.label} sx={{ mb: { xs: '9px', ultraHD: '12px' } }}>
                                      {field.label}<span style={{ color: 'red' }}>*</span>
                                    </Typography>
                                    <Select
                                      id={field.name}
                                      labelId={`${field.name}-label`}
                                      className={classes.textfield}
                                      value={goal[field.name] || ''}
                                      onChange={(e) => handleGoalChange(index, field.name, e.target.value)}
                                      placeholder={field.label}
                                      sx={{
                                        height: { xs: '45px', ultraHD: '60px' },
                                        fontSize: { xs: '12px', ultraHD: '16px' },
                                        borderRadius: { xs: '6px', ultraHD: '8px' },
                                      }}
                                    >
                                      {field.name === 'ChooseMentor' && mentors
                                        ? mentors.map((mentor) => (
                                          <MenuItem key={mentor.id} value={mentor.mentorshipId} sx={{ fontSize: { xs: '12px', ultraHD: '16px' } }}>
                                            {mentor.name} {/* Mentor name */}
                                          </MenuItem>
                                        ))
                                        : Array.isArray(field.options) &&
                                        field.options.map((option, index) => (
                                          <MenuItem key={index} value={option} sx={{ fontSize: { xs: '12px', ultraHD: '16px' } }}>
                                            {option}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                    {goalErrors[index] && goalErrors[index][field.name] && (
                                      <Typography variant="caption" color="error" sx={{ mt: 0.5, fontSize: '12px' }}>
                                        {goalErrors[index][field.name]}
                                      </Typography>
                                    )}
                                  </FormControl>
                                </Grid>
                              );
                            }
                            return null;
                          })}
                        </Grid>

                        {/* Goal Measurement */}
                        <Typography className={classes.milestonetitle}>Goal Measurement<span style={{ color: 'red' }}>*</span></Typography>
                        <Typography className={classes.milestonesubtitle}>How do you want to measure the progress of your goal.</Typography>

                        {/* Progress Measurement Radio Group */}
                        {formData.fields.map((field) => {
                          if (field.type === 'radio' && field.name === 'ProgressMeasurement') {
                            return (
                              <FormControl component="fieldset" key={field.name} sx={{ pl: '2px' }} error={!!(goalErrors[index] && goalErrors[index]['ProgressMeasurement'])}>
                                <RadioGroup 
                                  value={goal.ProgressMeasurement || ''} 
                                  onChange={(e: any) => handleGoalChange(index, 'ProgressMeasurement', e.target.value)} 
                                  sx={{
                                    flexDirection: { xs: 'column', sm: 'row' }
                                  }}
                                >
                                  {Array.isArray(field.options) &&
                                    field.options.length > 0 &&
                                    field.options.map((option, index) => (
                                      <FormControlLabel
                                        key={index}
                                        value={option}
                                        control={
                                          <Radio
                                            sx={{
                                              '& .MuiSvgIcon-root': {
                                                fontSize: { xs: '16px', ultraHD: '20px' },
                                              },
                                            }}
                                          />
                                        }
                                        label={option}
                                        className={classes.radioLabel}
                                      />
                                    ))}
                                </RadioGroup>
                                {goalErrors[index] && goalErrors[index]['ProgressMeasurement'] && (
                                  <Typography variant="caption" color="error" sx={{ mt: 0.5, fontSize: '12px' }}>
                                    {goalErrors[index]['ProgressMeasurement']}
                                  </Typography>
                                )}
                              </FormControl>
                            );
                          }
                          return null;
                        })}

                        {/* Conditional rendering based on ProgressMeasurement */}
                        {goal.ProgressMeasurement === 'Objective' ? (
                          /* Starting Value, Current Value, Target fields for Objective */
                          <>
                            <Grid container spacing={2}>
                              {formData.fields.map((field) => {
                                if (field.type === 'text' && (field.name === 'starting value' || field.name === 'current value')) {
                                  const goalFieldName = field.name === 'starting value' ? 'StartingValue' : 'CurrentValue';
                                  return (
                                    <Grid item xs={12} sm={6} key={field.name}>
                                      <Typography className={classes.label} sx={{ mb: { xs: '9px', ultraHD: '12px' } }}>
                                        {field.label}<span style={{ color: 'red' }}>*</span>
                                      </Typography>
                                      <TextField
                                        fullWidth
                                        type="number"
                                        placeholder={field.placeholder}
                                        value={goal[goalFieldName] || ''}
                                        onChange={(e) => handleGoalChange(index, goalFieldName, e.target.value)}
                                        error={!!(goalErrors[index] && goalErrors[index][goalFieldName])}
                                        helperText={goalErrors[index] && goalErrors[index][goalFieldName] ? goalErrors[index][goalFieldName] : ''}
                                        sx={{
                                          fontSize: { xs: '12px', ultraHD: '16px' },
                                          borderRadius: { xs: '6px', ultraHD: '8px' }
                                        }}
                                        InputProps={{
                                          sx: {
                                            height: { xs: '45px', ultraHD: '60px' },
                                            borderRadius: { xs: '6px', ultraHD: '8px' }
                                          }
                                        }}
                                      />
                                    </Grid>
                                  );
                                }
                                return null;
                              })}
                            </Grid>
                            <Grid container spacing={2}>
                              {formData.fields.map((field) => {
                                if (field.type === 'text' && field.name === 'target') {
                                  return (
                                    <Grid item xs={12} sm={6} key={field.name}>
                                      <Typography className={classes.label} sx={{ mb: { xs: '9px', ultraHD: '12px' } }}>
                                        {field.label}<span style={{ color: 'red' }}>*</span>
                                      </Typography>
                                      <TextField
                                        fullWidth
                                        type="number"
                                        placeholder={field.placeholder}
                                        value={goal.Target || ''}
                                        onChange={(e) => handleGoalChange(index, 'Target', e.target.value)}
                                        error={!!(goalErrors[index] && goalErrors[index]['Target'])}
                                        helperText={goalErrors[index] && goalErrors[index]['Target'] ? goalErrors[index]['Target'] : ''}
                                        sx={{
                                          fontSize: { xs: '12px', ultraHD: '16px' },
                                          borderRadius: { xs: '6px', ultraHD: '8px' }
                                        }}
                                        InputProps={{
                                          sx: {
                                            height: { xs: '45px', ultraHD: '60px' },
                                            borderRadius: { xs: '6px', ultraHD: '8px' }
                                          }
                                        }}
                                      />
                                    </Grid>
                                  );
                                }
                                return null;
                              })}
                            </Grid>
                          </>
                        ) : (
                          /* Progress Percentage field for Subjective */
                          formData.fields.map((field) => {
                            if (field.type === 'text' && field.name === 'Progress-Percentage') {
                              return (
                                <React.Fragment key={field.name}>
                                  <Typography className={classes.label}>{field.label}<span style={{ color: 'red' }}>*</span></Typography>
                                  <TextField
                                    className={classes.textfield}
                                    fullWidth
                                    value={goal.ProgressPercentage || ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (/^\d{1,3}%?$/.test(value) || value === '') {
                                        handleGoalChange(index, 'ProgressPercentage', value);
                                      }
                                    }}
                                    placeholder={field.placeholder}
                                    error={!!(goalErrors[index] && goalErrors[index]['ProgressPercentage'])}
                                    helperText={
                                      goalErrors[index] && goalErrors[index]['ProgressPercentage'] 
                                        ? goalErrors[index]['ProgressPercentage'] 
                                        : `Enter percentage (0-100)`
                                    }
                                    InputProps={{
                                      sx: {
                                        height: { xs: '45px', ultraHD: '60px' },
                                        borderRadius: { xs: '6px', ultraHD: '8px' },
                                      },
                                    }}
                                  />
                                </React.Fragment>
                              );
                            }
                            return null;
                          })
                        )}

                        {/* Goal Progress Checkbox */}
                        <FormControlLabel
                          className={classes.milestonesubtitle}
                          control={
                            <Checkbox
                              checked={goal.GoalProgress}
                              onChange={(e) => handleGoalChange(index, 'GoalProgress', e.target.checked)}
                              name="GoalProgress"
                              sx={{
                                pl: '12px',
                                '& .MuiSvgIcon-root': {
                                  fontSize: { xs: '16px', ultraHD: '20px' },
                                },
                              }}
                            />
                          }
                          label="Goal progress to add up to milestone progress"
                        />
                      </div>
                    ))}

                    {/* Add Goal Button */}
                    <Button 
                      variant="contained" 
                      startIcon={<AddIcon />} 
                      onClick={handleAddGoal} 
                      className={classes.addMilestoneBtn}
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                        mt: { xs: 2, sm: 1 }
                      }}
                    >
                      Add Goal
                    </Button>
                  </Box>

                  {/* Submit & Cancel Buttons */}
                  <Box
                    sx={{
                      position: { xs: 'fixed', sm: 'relative' },
                      bottom: { xs: 50, sm: 'auto' },
                      left: { xs: 0, sm: 'auto' },
                      right: { xs: 0, sm: 'auto' },
                      backgroundColor: { xs: 'white', sm: 'transparent' ,md: 'transparent',ultraHD: 'transparent'} ,
                      p: { xs: 2, sm: 0 },
                      zIndex: { xs: 1000, sm: 'auto' },
                      mt: { xs: 0, sm: 3 },
                    }}
                  >
                    <Grid container spacing={2} justifyContent="flex-end" alignItems="center">
                      <Grid item xs={6} sm="auto">
                        <Button
                          variant="outlined"
                          onClick={() => navigate.push(routes.goalsAndMilestones)}
                          sx={{
                            color: '#604797',
                            borderColor: '#604797',
                            fontSize: { xs: '12px', ultraHD: '16px' },
                            fontWeight: '500',
                            width: { xs: '100%', sm: 'auto' },
                            height: { xs: '40px', ultraHD: '42px' },
                            borderRadius: { xs: '8px', ultraHD: '8px' },
                            textTransform: 'none',
                            '&:hover': {
                              color: '#604797',
                              borderColor: '#604797',
                              backgroundColor: 'rgba(96, 71, 151, 0.04)',
                            },
                          }}
                        >
                          Cancel
                        </Button>
                      </Grid>
                      <Grid item xs={6} sm="auto">
                        <Button
                          variant="contained"
                          onClick={handleSubmit}
                          sx={{
                            backgroundColor: '#604797',
                            height: { xs: '40px', ultraHD: '42px' },
                            width: { xs: '100%', sm: '120px', ultraHD: '120px' },
                            fontSize: { xs: '12px', ultraHD: '16px' },
                            fontWeight: '600',
                            textTransform: 'none',
                            boxShadow: 'none',
                            borderRadius: { xs: '8px', ultraHD: '8px' },
                            '&:hover': {
                              backgroundColor: '#4c155f',
                              boxShadow: 'none',
                            },
                          }}
                        >
                          {isEditMode ? 'Update goals' : 'Add goals'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}
    </>
  );
};

export default AddMilestones;
